// import components
import { GridColDef, GridValidRowModel } from '@mui/x-data-grid-pro';

// import enums
import { BonusTypesEnums } from '../EmployeeBonuses.enums';

// import external libraries
import dayjs from 'dayjs';

// import hooks
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// import utils
import { dateFormatter } from 'utils/grid';
import { makeBuhColumns } from './useGetColumns.utils';

export const useGetColumns = ({ buhTypeSvc }) => {
  const { t } = useTranslation('common');

  let extraColumns;
  switch (buhTypeSvc) {
    case BonusTypesEnums.PAYMENT:
      extraColumns = [
        {
          headerName: t('amount'),
          field: 'payment',
          type: 'string',
          width: 60
        }
      ];
      break;
    case BonusTypesEnums.EXTRA_COMP:
      extraColumns = [
        {
          headerName: t('employeeBonuses.additionalWorkAllowanceHigh'),
          field: 'additionalWorkAllowanceHigh',
          type: 'string',
          width: 80
        },
        {
          headerName: t('employeeBonuses.additionalWorkAllowanceLow'),
          field: 'additionalWorkAllowanceLow',
          type: 'string',
          width: 80
        },
        {
          headerName: t('employeeBonuses.additionalWorkAllowanceSleep'),
          field: 'additionalWorkAllowanceSleep',
          type: 'string',
          width: 80
        },
        {
          headerName: t('employeeBonuses.additionalWorkAllowanceDeviant'),
          field: 'additionalWorkAllowanceDeviant',
          type: 'string',
          width: 80
        }
      ];
      break;
    default:
      extraColumns = [
        ...makeBuhColumns(t, 'string'),
        {
          headerName: t('total'),
          field: 'buhTotalHours',
          type: 'string',
          width: 60
        }
      ];
      break;
  }

  return useMemo(
    () => [
      {
        headerName: t('employeeBonuses.week'),
        field: 'weekNumber',
        type: 'number',
        width: 60
      },
      {
        headerName: t('date'),
        field: 'dutyDate',
        type: 'string',
        valueFormatter: dateFormatter
      },
      {
        headerName: t('organizationalUnit'),
        field: 'brName',
        type: 'string',
        width: 160
      },
      {
        headerName: t('wa'),
        field: 'waNr',
        type: 'number',
        align: 'center',
        headerAlign: 'center',
        width: 60
      },
      {
        headerName: t('period'),
        field: 'period',
        type: 'string',
        valueGetter: (_, row) => {
          if (row.rowType === 'totals') return;
          const start = dayjs(row.startTime).format('HH:mm');
          const end = dayjs(row.endTime).format('HH:mm');
          return `${start} - ${end}`;
        }
      },
      {
        headerName: t('hours'),
        field: 'hours',
        type: 'number',
        align: 'center',
        headerAlign: 'center',
        width: 80
      },
      {
        headerName: t('generatedReport.duty_type'),
        field: 'dutyTypeDescription',
        type: 'string',
        width: 120
      },
      {
        headerName: t('CLA'),
        field: 'claCode',
        type: 'string',
        align: 'center',
        headerAlign: 'center',
        width: 60
      },
      ...extraColumns,
      {
        headerName: t('status'),
        field: 'statusSvc',
        type: 'string',
        flex: 1,
        valueGetter: (_, row) => {
          if (row.rowType === 'totals') return;
          return t(`employeeBonuses.StatusSvc.${row?.statusSvc}`);
        }
      }
    ],
    [buhTypeSvc, t]
  ) as GridColDef<GridValidRowModel>[];
};
